import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { profileUpdateSchema, type ProfileUpdateFormValues } from '@emazy/shared-validation';

import { useFeedback } from '@/app/feedback';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

import { CustomerProfileApiError } from '../api';
import { AccountPageState, EmailChangeDialog } from '../components';
import { useCustomerProfile, useUpdateCustomerProfile } from '../hooks';

export function ProfilePage() {
  useDocumentMetadata('Profile Settings');
  const query = useCustomerProfile();
  const mutation = useUpdateCustomerProfile();
  const feedback = useFeedback();
  const [emailOpen, setEmailOpen] = useState(false);
  const [conflict, setConflict] = useState(false);
  const form = useForm<ProfileUpdateFormValues>({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: { firstName: '', lastName: '', locale: 'en-US', expectedVersion: 1 },
  });
  const profile = query.data?.profile;
  const firstName = useWatch({ control: form.control, name: 'firstName' });
  const lastName = useWatch({ control: form.control, name: 'lastName' });
  useEffect(() => {
    if (profile && !form.formState.isDirty)
      form.reset({
        firstName: profile.firstName,
        lastName: profile.lastName,
        locale: profile.locale,
        expectedVersion: profile.version,
      });
  }, [form, profile]);

  return (
    <AccountPageState
      loading={query.isLoading}
      error={query.isError}
      retry={() => void query.refetch()}
    >
      {profile && (
        <div className="account-page">
          <header>
            <p className="eyebrow">Personal details</p>
            <h2>Your profile</h2>
            <p>Keep the name and language we use throughout your Emazy account current.</p>
          </header>
          {conflict && (
            <div className="form-alert" role="alert">
              <strong>Your profile changed elsewhere.</strong>
              <p>Your edits are still here. Reload only when you’re ready to replace them.</p>
              <button
                type="button"
                onClick={() => {
                  form.reset({
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    locale: profile.locale,
                    expectedVersion: profile.version,
                  });
                  setConflict(false);
                }}
              >
                Reload current profile
              </button>
            </div>
          )}
          <form
            className="account-form"
            noValidate
            onSubmit={form.handleSubmit(async (values) => {
              setConflict(false);
              try {
                const updated = await mutation.mutateAsync(values);
                if (updated && typeof updated === 'object' && 'version' in updated)
                  form.reset({ ...values, expectedVersion: updated.version as number });
                feedback.showSuccess('Profile updated', 'Your account details have been saved.');
              } catch (error) {
                if (error instanceof CustomerProfileApiError && error.code === 'profile-conflict')
                  setConflict(true);
              }
            })}
          >
            <div className="account-form__grid">
              <label>
                <span>First name</span>
                <input
                  autoComplete="given-name"
                  aria-invalid={Boolean(form.formState.errors.firstName)}
                  {...form.register('firstName')}
                />
                {form.formState.errors.firstName && (
                  <small role="alert">{form.formState.errors.firstName.message}</small>
                )}
              </label>
              <label>
                <span>Last name</span>
                <input
                  autoComplete="family-name"
                  aria-invalid={Boolean(form.formState.errors.lastName)}
                  {...form.register('lastName')}
                />
                {form.formState.errors.lastName && (
                  <small role="alert">{form.formState.errors.lastName.message}</small>
                )}
              </label>
            </div>
            <label>
              <span>Display name</span>
              <input
                value={`${firstName} ${lastName}`.trim()}
                readOnly
                aria-describedby="display-name-help"
              />
              <small id="display-name-help">Built from your first and last name.</small>
            </label>
            <label>
              <span>Verified email</span>
              <input type="email" value={profile.email} readOnly />
              <button className="text-action" type="button" onClick={() => setEmailOpen(true)}>
                Change email
              </button>
            </label>
            <label>
              <span>Language and region</span>
              <select {...form.register('locale')}>
                <option value="en-US">English (United States)</option>
                <option value="tr-TR">Türkçe (Türkiye)</option>
              </select>
            </label>
            <input type="hidden" {...form.register('expectedVersion', { valueAsNumber: true })} />
            <button
              className="button-link button-link--primary"
              disabled={mutation.isPending || !form.formState.isDirty}
            >
              {mutation.isPending ? 'Saving…' : 'Save profile'}
            </button>
          </form>
          <EmailChangeDialog open={emailOpen} onClose={() => setEmailOpen(false)} />
        </div>
      )}
    </AccountPageState>
  );
}
